import { useState } from "react";
import { downloadMimeType } from "@touchworks/shared/support/responseBlocks.js";
// Mesmo catalogo que desenha a biblioteca do editor: o nome no diagrama e o
// nome na paleta nao tem como divergir.
import { nodeByType } from "@touchworks/shared/flow/nodeCatalog.js";
import { nodeIcon } from "@/components/flow/nodeIcons.jsx";
import ChannelCard from "@/components/channels/ChannelCard.jsx";
import { navIcon } from "@/components/nav/navIcons.jsx";
import { navItem } from "@touchworks/shared/platform/navigation.js";

/**
 * The rich part of a Lucas answer.
 *
 * Every block carries a `fallbackText`, and anything this component does not
 * recognise renders as that text rather than disappearing. A turn written by a
 * newer model must stay readable on an older screen.
 *
 * Downloads are assembled locally with a Blob: the content already travelled
 * with the message, so a round-trip would only add a way to fail.
 */

function Unknown({ block }) {
  return (
    <p className="m-0 whitespace-pre-line text-[15px] leading-[1.7] text-[#25273a] dark:text-white/85">
      {block.fallbackText}
    </p>
  );
}

/**
 * Cor do nó — por extenso, como na paleta do editor.
 *
 * Interpolar `bg-${cor}-500/12` some do bundle: o Tailwind varre o código-fonte
 * e não encontra a string montada em runtime.
 */
const NODE_COLOR = {
  emerald: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-400",
  sky: "bg-sky-500/12 text-sky-600 dark:text-sky-400",
  violet: "bg-violet-500/12 text-violet-600 dark:text-violet-400",
  pink: "bg-pink-500/12 text-pink-600 dark:text-pink-400",
  gray: "bg-black/[.05] text-[#5c5f70] dark:bg-white/[.08] dark:text-white/60",
  yellow: "bg-yellow-500/12 text-yellow-700 dark:text-yellow-400",
  green: "bg-green-500/12 text-green-700 dark:text-green-400",
  purple: "bg-purple-500/12 text-purple-600 dark:text-purple-400",
  fuchsia: "bg-fuchsia-500/12 text-fuchsia-600 dark:text-fuchsia-400",
  indigo: "bg-indigo-500/12 text-indigo-600 dark:text-indigo-400",
  amber: "bg-amber-500/12 text-amber-700 dark:text-amber-400",
  cyan: "bg-cyan-500/12 text-cyan-700 dark:text-cyan-400",
  teal: "bg-teal-500/12 text-teal-700 dark:text-teal-400",
  orange: "bg-orange-500/12 text-orange-700 dark:text-orange-400",
  blue: "bg-blue-500/12 text-blue-600 dark:text-blue-400",
};

/**
 * O caminho dos nós, desenhado.
 *
 * O modelo emitiu só os tipos. O nome sai daqui, do mesmo catálogo que monta a
 * biblioteca do editor — então o que está escrito no diagrama é exatamente o
 * que a pessoa vai procurar para arrastar. Ela não precisa traduzir nada.
 *
 * Tipo fora do catálogo não vira card. O servidor já descarta, mas a tela não
 * pode depender disso: um backend mais novo pode mandar um nó que este bundle
 * ainda não conhece, e um retângulo sem nome não ajuda ninguém.
 */
function FlowBlock({ block }) {
  const steps = (block.steps || [])
    .map((step) => {
      const node = nodeByType(step.type);
      return { ...step, node, Icon: node ? nodeIcon(node.icon) : null };
    })
    .filter((step) => step.node);

  if (!steps.length) return <Unknown block={block} />;

  return (
    <div className="flex flex-wrap items-start gap-1.5 rounded-[12px] border border-black/[.07] bg-black/[.02] p-3 dark:border-white/[.10] dark:bg-white/[.03]">
      {steps.map((step, index) => (
        <div key={`${step.type}-${index}`} className="flex items-start gap-1.5">
          <div className="flex max-w-[150px] flex-col gap-1">
            <span
              data-node-step={step.type}
              className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12.5px] font-semibold leading-tight ${
                NODE_COLOR[step.node.color] || NODE_COLOR.gray
              }`}
            >
              {/* O mesmo ícone da Biblioteca: é por ele que a pessoa varre o
                  painel, então um ícone diferente a faria procurar outra coisa. */}
              {step.Icon && <step.Icon className="h-3.5 w-3.5 shrink-0" />}
              {step.node.label}
            </span>
            {step.note && (
              <span className="px-0.5 text-[11.5px] leading-[1.35] text-[#5c5f70] dark:text-white/50">
                {step.note}
              </span>
            )}
          </div>
          {index < steps.length - 1 && (
            <span
              data-step-arrow=""
              aria-hidden="true"
              className="mt-1.5 select-none text-[13px] text-[#9a9db0] dark:text-white/35"
            >
              →
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Os fluxos da conta, para apontar qual é.
 *
 * A pessoa não exporta, não baixa e não anexa nada: o Lucas descobre os fluxos
 * e mostra quais são. O clique manda a mensagem que o cartão carrega, então a
 * conversa continua sem ela digitar o nome — e sem risco de digitar errado.
 *
 * Mesmo conteúdo do card do Touch Builder: ícone, título e descrição. O ícone
 * é o padrão (`Zap`, "Automação"); lá o card ainda troca pelo logo do provedor
 * quando o fluxo está ligado a uma integração, e isso não é replicado aqui.
 */
function CardsBlock({ block, onPick }) {
  const items = block.items || [];
  if (!items.length) return <Unknown block={block} />;

  return (
    <div className="flex flex-col gap-1.5">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          data-flow-card=""
          onClick={() => onPick?.(item.message)}
          className="flex w-full items-start gap-2.5 rounded-[12px] border border-black/[.08] bg-white px-3 py-2.5 text-left transition-colors hover:bg-black/[.03] dark:border-white/[.12] dark:bg-white/[.05] dark:hover:bg-white/[.09]"
        >
          <span className="mt-0.5 shrink-0 text-[#6366f1] dark:text-indigo-400">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4" aria-hidden="true">
              <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-[13.5px] font-semibold text-[#15161f] dark:text-white">
              {item.title}
            </span>
            {item.description && (
              <span className="truncate text-[12px] text-[#5c5f70] dark:text-white/55">
                {item.description}
              </span>
            )}
          </span>
        </button>
      ))}
      {/* A lista é cortada quando a conta tem muitos fluxos; sem este aviso,
          quem tem problema no fluxo que não coube fica sem saída — o texto da
          resposta não lista mais os nomes. */}
      {block.note && (
        <p className="m-0 px-1 text-[12px] text-[#5c5f70] dark:text-white/55">{block.note}</p>
      )}
    </div>
  );
}

/**
 * O caminho na tela, com o ícone que a pessoa vai procurar.
 *
 * Ela lê "Kanban" e varre a barra lateral atrás da palavra. O que o olho acha
 * primeiro é o ícone — então ele vem junto, e é o MESMO componente que o menu
 * desenha. Um ícone parecido a faria procurar outra coisa.
 *
 * O modelo emitiu só a chave do item. Rótulo e ícone saem do catálogo de
 * navegação aqui, como no diagrama de nós: ele não escreve o nome, então não
 * tem como escrevê-lo errado — foi assim que "Kanban" virou "CRM".
 */
function PathBlock({ block, onNavigate }) {
  /**
   * O passo de menu vira ATALHO, não só rótulo.
   *
   * Mostrar "Canais" e deixar a pessoa procurar é meio caminho — ela já sabia
   * o nome, o que faltava era chegar lá. O endereço vem PRONTO do servidor, no
   * próprio passo, como nos cartões de canal: montar aqui exigiria a conta e o
   * catálogo de rotas dentro de um componente que só desenha.
   */
  const steps = (block.path || [])
    .map((step) => {
      if (!step.nav) return step.text ? { label: step.text } : null;
      const item = navItem(step.nav);
      if (!item) return null;
      return { label: item.label, Icon: navIcon(item.icon), href: step.href || null };
    })
    .filter(Boolean);

  if (!steps.length) return <Unknown block={block} />;

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-[12px] border border-black/[.07] bg-black/[.02] p-3 dark:border-white/[.10] dark:bg-white/[.03]">
      {steps.map((step, index) => (
        <div key={`${step.label}-${index}`} className="flex items-center gap-1.5">
          {(() => {
            const classe = `inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12.5px] leading-tight ${
              step.Icon
                ? "bg-blue-500/12 font-semibold text-blue-700 dark:text-blue-400"
                : "bg-black/[.05] text-[#5c5f70] dark:bg-white/[.08] dark:text-white/70"
            }`;
            const conteudo = (
              <>
                {step.Icon && <step.Icon className="h-3.5 w-3.5 shrink-0" />}
                {step.label}
              </>
            );

            // Sem endereço, ou sem quem navegue, não vira botão: um atalho que
            // não leva a lugar nenhum é pior que um rótulo honesto.
            return step.href && onNavigate ? (
              <button
                type="button"
                onClick={() => onNavigate(step.href)}
                data-path-step={step.label}
                className={`${classe} underline-offset-2 hover:underline`}
              >
                {conteudo}
              </button>
            ) : (
              <span data-path-step={step.label} className={classe}>
                {conteudo}
              </span>
            );
          })()}
          {index < steps.length - 1 && (
            <span
              aria-hidden="true"
              className="select-none text-[13px] text-[#9a9db0] dark:text-white/35"
            >
              →
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Os canais da conta, para escolher em qual criar o template.
 *
 * Template de WhatsApp é aprovado por CANAL: não existe "a página de
 * templates", existe uma por canal. Por isso a escolha vem antes do link, e
 * cada cartão já leva pronta a página do seu número.
 *
 * O cartão é o MESMO componente da tela de Integrações — a pessoa reconhece o
 * objeto, não só o número. Uma segunda versão do cartão a faria duvidar de qual
 * é qual.
 *
 * Foto, número, estado e qualidade vêm prontos do servidor, do mesmo serviço
 * que alimenta a tela de Integrações — o cartão do chat não pode mostrar um
 * estado diferente do cartão de lá.
 *
 * Rolagem horizontal porque conta com muitos números é comum, e empilhar doze
 * cartões enterraria a resposta que está logo abaixo. `custom-scrollbar` é a
 * barra do produto: a do sistema aparece cinza e larga no meio da conversa.
 */
/**
 * O destino do clique vem do PROPÓSITO do bloco, não do componente.
 *
 * O cartão tinha o endereço de templates cravado: clicar num diagnóstico de
 * envio levava a pessoa para criar template, sem ter respondido nada. Onde ela
 * precisa apontar o canal, o clique responde ao Lucas; onde o assunto é
 * template ou configuração do número, ele abre a tela certa.
 */
function ChannelsBlock({ block, onPick }) {
  const items = block.items || [];
  if (!items.length) return <Unknown block={block} />;

  const purpose = block.purpose || "pick";

  const destino = (item) => {
    if (purpose === "templates") return { href: item.templatesUrl || item.url };
    if (purpose === "settings") return { href: item.settingsUrl };
    // `pick`: a resposta é a escolha. O texto identifica o canal pelo nome e
    // pelo número, que é o que desempata dois canais de nome parecido.
    return {
      onClick: () =>
        onPick?.(
          item.number
            ? `O canal é ${item.name} (${item.number}).`
            : `O canal é ${item.name}.`,
        ),
    };
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="custom-scrollbar -mx-1 flex snap-x gap-2.5 overflow-x-auto px-1 pb-1.5">
        {items.map((item) => (
          <div key={item.id} className="w-[240px] shrink-0 snap-start">
            <ChannelCard
              {...destino(item)}
              name={item.name}
              number={item.number}
              profilePic={item.profilePic}
              status={item.status}
              quality={item.quality}
              fallbackAvatar={
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-green-500/20 bg-green-500/10">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-green-600 dark:text-green-400" aria-hidden="true">
                    <path d="M12 2a10 10 0 0 0-8.6 15l-1.3 4.8 4.9-1.3A10 10 0 1 0 12 2zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .1-1.7-.1a12 12 0 0 1-5.6-4.9c-.4-.6-.9-1.5-.9-2.4 0-.9.5-1.4.7-1.6.2-.2.4-.3.6-.3h.5c.2 0 .4 0 .6.4l.8 1.9c.1.2 0 .4-.1.5l-.4.5c-.1.1-.3.3-.1.6.2.3.7 1.2 1.5 1.9 1 .9 1.8 1.1 2.1 1.3.2.1.4 0 .5-.1l.7-.8c.2-.2.3-.2.6-.1l1.8.9c.3.1.4.2.5.3v1z" />
                  </svg>
                </div>
              }
            />
          </div>
        ))}
      </div>
      {block.note && (
        <p className="m-0 px-1 text-[12px] text-[#5c5f70] dark:text-white/55">{block.note}</p>
      )}
    </div>
  );
}

function TextBlock({ block }) {
  return (
    <p className="m-0 whitespace-pre-line text-[15px] leading-[1.7] text-[#25273a] dark:text-white/85">
      {block.text || block.fallbackText}
    </p>
  );
}

function ImageBlock({ block }) {
  return (
    <figure className="m-0">
      <img
        src={block.url}
        alt={block.alt || ""}
        className="max-h-[320px] w-full rounded-[12px] border border-black/10 object-cover dark:border-white/[.14]"
      />
      {block.caption && (
        <figcaption className="mt-1.5 text-[12.5px] text-[#5c5f70] dark:text-white/50">
          {block.caption}
        </figcaption>
      )}
    </figure>
  );
}

function VideoBlock({ block }) {
  return (
    <figure className="m-0">
      {/* `controls` sem `autoPlay`: vídeo que começa sozinho dentro de uma
          conversa de suporte atropela quem está lendo a resposta. */}
      <video
        src={block.url}
        controls
        preload="metadata"
        className="max-h-[360px] w-full rounded-[12px] border border-black/10 dark:border-white/[.14]"
      >
        <track kind="captions" />
      </video>
      {block.caption && (
        <figcaption className="mt-1.5 text-[12.5px] text-[#5c5f70] dark:text-white/50">
          {block.caption}
        </figcaption>
      )}
    </figure>
  );
}

function DecisionBlock({ block, onPick }) {
  return (
    <div className="rounded-[14px] border border-black/[.08] bg-black/[.02] p-4 dark:border-white/[.12] dark:bg-white/[.04]">
      <p className="m-0 text-[14px] font-semibold leading-[1.45] text-[#15161f] dark:text-white">
        {block.question}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {block.options.map((option) => (
          <button
            key={option.label}
            type="button"
            onClick={() => onPick?.(option.message)}
            className="inline-flex h-8 items-center whitespace-nowrap rounded-2xl border border-black/[.12] bg-white px-3.5 text-[12.5px] font-semibold text-[#15161f] transition-colors hover:bg-black/[.04] dark:border-white/20 dark:bg-white/[.07] dark:text-white dark:hover:bg-white/[.13]"
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function SnippetBlock({ block }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard?.writeText(block.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="overflow-hidden rounded-[12px] border border-black/[.08] dark:border-white/[.12]">
      <div className="flex items-center gap-2 border-b border-black/[.06] bg-black/[.025] px-3 py-1.5 dark:border-white/[.08] dark:bg-white/[.04]">
        <span className="font-mono text-[10.5px] uppercase tracking-[.1em] text-[#6c6f80] dark:text-white/45">
          {block.nodeType || block.language || "trecho"}
        </span>
        <button
          type="button"
          onClick={copy}
          className="ml-auto text-[11.5px] font-semibold text-[#5c5f70] transition-colors hover:text-[#15161f] dark:text-white/50 dark:hover:text-white"
        >
          {copied ? "Copiado" : "Copiar"}
        </button>
      </div>
      <pre className="m-0 overflow-x-auto px-3 py-2.5 font-mono text-[12.5px] leading-[1.6] text-[#25273a] dark:text-white/85">
        {block.code}
      </pre>
    </div>
  );
}

function DownloadBlock({ block }) {
  const download = () => {
    const blob = new Blob([block.content], {
      type: downloadMimeType(block.format),
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = block.filename;
    anchor.click();
    // Sem revoke o Blob fica retido enquanto a aba viver.
    URL.revokeObjectURL(url);
  };

  return (
    <button
      type="button"
      onClick={download}
      className="inline-flex h-9 items-center gap-2 rounded-xl border border-black/[.12] bg-white px-3.5 text-[13px] font-semibold text-[#15161f] transition-colors hover:bg-black/[.04] dark:border-white/20 dark:bg-white/[.07] dark:text-white dark:hover:bg-white/[.13]"
    >
      <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3.5 w-3.5">
        <path
          d="M8 2.5v8M4.5 7.5L8 11l3.5-3.5M2.5 13.5h11"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {block.filename}
    </button>
  );
}

/** O link que resolve o problema, como botão. Sempre em nova aba: a pessoa
 *  está no meio de uma conversa e não pode perdê-la ao ir resolver. */
function ActionBlock({ block }) {
  return (
    <a
      href={block.url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex h-9 items-center gap-2 self-start rounded-xl bg-[#15161f] px-4 text-[13px] font-bold text-white no-underline transition-opacity hover:opacity-90 dark:bg-white dark:text-[#0a0a0f]"
    >
      {block.label}
      <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3 w-3">
        <path
          d="M6 3h7v7M13 3L4 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </a>
  );
}

/**
 * O diagnóstico do fluxo — desenhado pela plataforma, não escrito pelo modelo.
 *
 * A mesma pergunta sobre o mesmo fluxo voltava com respostas diferentes a cada
 * tentativa. O que está errado é fato, e fato tem forma fixa: erro que impede
 * de rodar em cima, aviso de estrutura embaixo, o id do nó do lado para ela
 * achar na tela.
 */
function ProblemsBlock({ block }) {
  const linha = (p, i, grave) => (
    <li key={`${p.nodeId || "x"}-${i}`} className="flex gap-2 leading-[1.45]">
      <span className={grave ? "text-rose-500" : "text-amber-500"}>•</span>
      <span>
        {p.message}
        {p.nodeId && (
          <code className="ml-1.5 rounded bg-black/[.06] px-1 py-0.5 text-[11px] text-foreground-muted dark:bg-white/10">
            {p.nodeId}
          </code>
        )}
      </span>
    </li>
  );

  return (
    <div className="rounded-[12px] border border-border bg-surface px-3.5 py-3 text-[13px]">
      {block.errors?.length > 0 && (
        <>
          <p className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-rose-500">
            Impede o fluxo de rodar
          </p>
          <ul className="flex flex-col gap-1.5">
            {block.errors.map((p, i) => linha(p, i, true))}
          </ul>
        </>
      )}
      {block.warnings?.length > 0 && (
        <>
          <p className="mb-1.5 mt-3 text-[12px] font-semibold uppercase tracking-wide text-amber-500">
            Estrutura
          </p>
          <ul className="flex flex-col gap-1.5">
            {block.warnings.map((p, i) => linha(p, i, false))}
          </ul>
        </>
      )}
    </div>
  );
}

const RENDERERS = {
  problems: ProblemsBlock,
  text: TextBlock,
  flow: FlowBlock,
  cards: CardsBlock,
  channels: ChannelsBlock,
  path: PathBlock,
  action: ActionBlock,
  image: ImageBlock,
  video: VideoBlock,
  decision: DecisionBlock,
  snippet: SnippetBlock,
  download: DownloadBlock,
};

export default function MessageBlocks({ blocks = [], onPick, onNavigate }) {
  if (!blocks.length) return null;

  return (
    <div className="flex flex-col gap-3">
      {blocks.map((block, index) => {
        const Renderer = RENDERERS[block.type] || Unknown;
        return (
          <Renderer
            key={`${block.type}-${index}`}
            block={block}
            onPick={onPick}
            onNavigate={onNavigate}
          />
        );
      })}
    </div>
  );
}
